import { useCallback, useEffect, useState, Fragment } from "react";
import { Link } from "react-router-dom";import { toast } from "sonner";
import { AlertTriangle, ShieldAlert, ShieldCheck, Trash2, UserPlus, Undo2, SlidersHorizontal } from "lucide-react";
import { Shell } from "@/components/Shell";
import { InstrumentChecklist } from "@/components/InstrumentChecklist";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { DeleteTraderDialog } from "@/components/DeleteTraderDialog";
import { Masked, maskEmail } from "@/components/Masked";
import { MobileRow } from "@/components/MobileRow";
import { api, errMsg } from "@/lib/api";

const Label = ({ children }) => (
  <label className="block text-[10px] tracking-[0.14em] uppercase font-semibold text-zinc-500 mb-1.5">
    {children}
  </label>
);
const inputCls =
  "w-full rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 focus:border-blue-500 focus:outline-none transition-colors duration-200";
const money = (v) => `${v >= 0 ? "+" : "-"}$${Math.abs(v ?? 0).toFixed(2)}`;
export default function Admin() {
  const [form, setForm] = useState(null);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [invite, setInvite] = useState({ email: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [deleting, setDeleting] = useState(null);
  const [purging, setPurging] = useState(false);

  // Live data only — never touches the template form the admin may be editing.
  const loadLive = useCallback(async () => {
    const [u, i, t] = await Promise.all([
      api.get("/admin/users"),
      api.get("/admin/invites"),
      api.get("/admin/template"),
    ]);
    setUsers(u.data);
    setInvites(i.data);
    setForm((f) => (f ? { ...f, kill_switch: t.data.kill_switch } : t.data));
  }, []);
  const load = useCallback(async () => {
    const t = await api.get("/admin/template");
    setForm(t.data);
    try {
      const { data } = await api.get("/admin/instrument-catalog");
      setCatalog((data.symbols || []).map((s) => s.name));
    } catch {
      setCatalog([]);
    }
    await loadLive();
  }, [loadLive]);

  useEffect(() => {
    load();
  }, [load]);

  const [resyncing, setResyncing] = useState(false);
  const resyncCatalog = async () => {    setResyncing(true);
    try {
      const { data } = await api.post("/admin/instrument-catalog/resync");
      await load();
      if (data.ok) {
        // Answering with nothing new is a success: the picker is already current.
        toast.success(`${data.ok} EA${data.ok > 1 ? "s" : ""} re-read MT5 · list is up to date`);
      } else if (!data.asked) {
        toast.info("No trader has a pod slot yet, so there is no EA to ask");
      } else {
        toast.error(data.error || "The EA did not reply — check it is connected");
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setResyncing(false);
    }
  };

  useEffect(() => {
    const id = setInterval(loadLive, 10000);
    return () => clearInterval(id);
  }, [loadLive]);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value,
    }));

  const saveTemplate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings", form);
      setForm(data);
      toast.success("Rule template saved — applied to every trader on the template");
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleGlobalKill = async () => {
    try {
      const { data } = await api.post("/settings/kill-switch", { active: !form.kill_switch });
      setForm((f) => ({ ...f, kill_switch: data.kill_switch }));
      data.kill_switch
        ? toast.error("GLOBAL KILL SWITCH ACTIVE — every trader is blocked")
        : toast.success("Global Kill Switch reset");
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const toggleUserKill = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/kill-switch`, { active: !u.kill_switch });
      toast[u.kill_switch ? "success" : "error"](
        u.kill_switch ? `${maskEmail(u.email)} can trade again` : `${maskEmail(u.email)} is blocked from trading`
      );
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const addInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/invites", invite);
      toast.success(`${maskEmail(invite.email)} invited`);
      setInvite({ email: "", role: "user" });
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeInvite = async (email) => {
    try {
      await api.delete(`/admin/invites/${encodeURIComponent(email)}`);
      toast.success(`Invite for ${maskEmail(email)} removed`);
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const revoke = async (u) => {
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success(`${maskEmail(u.email)} removed`);
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const purge = async (typed) => {
    setPurging(true);
    try {
      await api.delete(
        `/admin/users/${deleting.user_id}/purge?confirm_email=${encodeURIComponent(typed)}`
      );
      toast.success(`${maskEmail(deleting.email)} and all their data deleted`);
      setDeleting(null);
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPurging(false);
    }
  };

  const restore = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/restore`);
      toast.success(`${maskEmail(u.email)} restored`);
      loadLive();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  if (!form) return <Shell />;

  const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] font-semibold text-zinc-500 whitespace-nowrap";
  const td = "px-3 py-2.5 font-mono text-xs text-zinc-200 whitespace-nowrap";
  const traders = users.filter((u) => u.role !== "admin");
  const traderActions = (u) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        data-testid={`user-kill-switch-btn-${u.email}`}
        onClick={() => toggleUserKill(u)}
        className={`px-2 py-1 rounded-sm text-[10px] uppercase font-semibold tracking-wide border transition-colors duration-200 ${
          u.kill_switch
            ? "border-emerald-700 text-emerald-400 hover:bg-emerald-600 hover:text-white"
            : "border-red-800 text-red-300 hover:bg-red-600 hover:text-white"
        }`}
      >
        {u.kill_switch ? "Unblock" : "Block"}
      </button>
      <Link
        data-testid={`toggle-override-btn-${u.email}`}
        to={`/admin/traders/${u.user_id}`}
        className="border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-2 py-1 rounded-sm text-[10px] uppercase font-semibold tracking-wide transition-colors duration-200"
      >
        Rules &amp; trades
      </Link>
      {u.status === "revoked" ? (
        <button
          data-testid={`restore-user-btn-${u.email}`}
          onClick={() => restore(u)}
          className="flex items-center gap-1 border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-2 py-1 rounded-sm text-[10px] uppercase font-semibold transition-colors duration-200"
        >
          <Undo2 className="h-3 w-3" /> Restore
        </button>
      ) : (
        <button
          data-testid={`remove-user-btn-${u.email}`}
          onClick={() => revoke(u)}
          className="flex items-center gap-1 border border-red-800 text-red-300 hover:bg-red-600 hover:text-white px-2 py-1 rounded-sm text-[10px] uppercase font-semibold transition-colors duration-200"
        >
          <Undo2 className="h-3 w-3" /> Remove
        </button>
      )}
      <button
        data-testid={`delete-user-btn-${u.user_id}`}
        onClick={() => setDeleting(u)}
        title="Delete permanently, with all their data"
        className="flex items-center gap-1 bg-red-600/90 hover:bg-red-500 text-white px-2 py-1 rounded-sm text-[10px] uppercase font-semibold transition-colors duration-200"
      >
        <Trash2 className="h-3 w-3" /> Delete
      </button>
    </div>
  );
  const statusText = (u) =>
    u.status === "revoked"
      ? "removed"
      : u.kill_switch
      ? "blocked"
      : u.day_stopped
      ? u.day_stop_reason === "auto"
        ? "limit hit"
        : "day closed"
      : "active";
  const pnlTone = (v) => (v > 0 ? "text-emerald-400" : v < 0 ? "text-red-400" : "text-zinc-200");

  return (
    <Shell>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            Admin Control
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Manage traders, set the rule template and watch performance. Admins do not trade.
          </p>
        </div>

        <CollapsibleCard
          title={`Global Kill Switch · ${form.kill_switch ? "ACTIVE" : "OFF"}`}
          icon={form.kill_switch ? ShieldAlert : ShieldCheck}
          storageKey="admin-kill-switch"
          testid="kill-switch-panel"
        >
          <div className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {form.kill_switch ? (
                <ShieldAlert className="h-6 w-6 text-red-400" />
              ) : (
                <ShieldCheck className="h-6 w-6 text-emerald-400" />
              )}
              <div>
                <p className="text-sm font-semibold">
                  Global Kill Switch · {form.kill_switch ? "ACTIVE" : "OFF"}
                </p>
                <p className="text-xs text-zinc-500">
                  {form.kill_switch
                    ? "Every trader is blocked from opening new trades."
                    : "Traders may trade within their effective limits."}
                </p>
              </div>
            </div>
            <button
              data-testid="kill-switch-btn"
              onClick={toggleGlobalKill}
              className={`px-5 py-2.5 rounded-sm text-xs font-bold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95 ${
                form.kill_switch
                  ? "border border-emerald-600 text-emerald-400 hover:bg-emerald-600 hover:text-white"
                  : "border-2 border-red-700 bg-red-950 text-red-300 hover:bg-red-600 hover:text-white shadow-[0_0_15px_rgba(239,68,68,0.35)]"
              }`}
            >
              {form.kill_switch ? "Reset Kill Switch" : "Activate Kill Switch"}
            </button>
          </div>
        </CollapsibleCard>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
          <CollapsibleCard
            title="Rule Template · default risk for every trader"
            icon={SlidersHorizontal}
            storageKey="admin-template"
            testid="risk-settings-card"
          >
          <form
            onSubmit={saveTemplate}
            data-testid="risk-settings-form"
            className="p-5 flex flex-col gap-5"
          >
            <div>
              <p className="text-[11px] text-zinc-600">
                Applies to all traders unless you override them individually below.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Risk method</Label>
                <select
                  data-testid="risk-method-select"
                  className={inputCls}
                  value={form.risk_method}
                  onChange={set("risk_method")}
                >
                  <option value="fixed">Fixed dollar per trade</option>
                  <option value="percent">Percent of balance</option>
                </select>
              </div>
              {form.risk_method === "fixed" ? (
                <div>
                  <Label>Risk per trade ($)</Label>
                  <input
                    data-testid="risk-fixed-input"
                    type="number"
                    step="1"
                    min="1"
                    className={inputCls}
                    value={form.risk_fixed_amount}
                    onChange={set("risk_fixed_amount")}
                  />
                </div>
              ) : (
                <div>
                  <Label>Risk per trade (% of balance)</Label>
                  <input
                    data-testid="risk-percent-input"
                    type="number"
                    step="0.1"
                    min="0.1"
                    className={inputCls}
                    value={form.risk_percent}
                    onChange={set("risk_percent")}
                  />
                </div>
              )}
              <div>
                <Label>Daily loss limit ($)</Label>
                <input
                  data-testid="daily-loss-limit-input"
                  type="number"
                  step="1"
                  min="1"
                  className={inputCls}
                  value={form.daily_loss_limit}
                  onChange={set("daily_loss_limit")}
                />
              </div>
              <div>
                <Label>Loss limit mode</Label>
                <select
                  data-testid="loss-limit-mode-select"
                  className={inputCls}
                  value={form.loss_limit_mode}
                  onChange={set("loss_limit_mode")}
                >
                  <option value="fixed">Fixed — profit does not extend it</option>
                  <option value="trailing">Grows with booked profit</option>
                </select>
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  {form.loss_limit_mode === "trailing"
                    ? "Booked profit is added to the day's loss room (base + realised profit)."
                    : "Day stops once total loss reaches the limit, whatever was booked."}
                </p>
              </div>
              <div>
                <Label>Max open trades</Label>
                <input
                  data-testid="max-open-trades-input"
                  type="number"
                  step="1"
                  min="1"
                  className={inputCls}
                  value={form.max_open_trades}
                  onChange={set("max_open_trades")}
                />
              </div>
              <div>
                <Label>Max trades per day</Label>
                <input
                  data-testid="max-trades-per-day-input"
                  type="number"
                  step="1"
                  min="0"
                  className={inputCls}
                  value={form.max_trades_per_day ?? 0}
                  onChange={set("max_trades_per_day")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  0 = unlimited. Counts every trade opened today (IST), including limit orders.
                </p>
              </div>
              <div>
                <Label>Daily profit target ($)</Label>
                <input
                  data-testid="daily-profit-target-input"
                  type="number"
                  step="1"
                  min="0"
                  className={inputCls}
                  value={form.daily_profit_target ?? 0}
                  onChange={set("daily_profit_target")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  0 = off. New trades are blocked once booked profit reaches this target.
                </p>
              </div>
              <div>
                <Label>Window start (IST)</Label>
                <input
                  data-testid="window-start-input"
                  type="time"
                  className={inputCls}
                  value={form.window_start}
                  onChange={set("window_start")}
                />
              </div>
              <div>
                <Label>Window end (IST)</Label>
                <input
                  data-testid="window-end-input"
                  type="time"
                  className={inputCls}
                  value={form.window_end}
                  onChange={set("window_end")}
                />
              </div>
              <div>
                <Label>TP1 close ratio (%)</Label>
                <input
                  data-testid="tp1-ratio-input"
                  type="number"
                  step="1"
                  min="0"
                  max="99"
                  className={inputCls}
                  value={form.tp1_ratio}
                  onChange={set("tp1_ratio")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Remaining {100 - Number(form.tp1_ratio || 0)}% runs to TP2 (native MT5 TP).
                  {Number(form.tp1_ratio || 0) === 0 && " 0% = TP1 banks nothing."}
                </p>
              </div>
              <div>
                <Label>Move SL to break-even at TP1</Label>
                <select
                  data-testid="move-be-select"
                  className={inputCls}
                  value={form.move_be_on_tp1 === false ? "off" : "on"}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, move_be_on_tp1: e.target.value === "on" }))
                  }
                >
                  <option value="on">On — stop goes to entry</option>
                  <option value="off">Off — stop stays put</option>
                </select>
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Off keeps the original SL after TP1. Trailing is unaffected.
                </p>
              </div>
              <div>
                <Label>Default TP1 (R multiple)</Label>
                <input
                  data-testid="tp1-r-input"
                  type="number"
                  step="0.1"
                  min="0"
                  className={inputCls}
                  value={form.tp1_r}
                  onChange={set("tp1_r")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  0 = no auto TP1. Auto TP1 = entry ± {form.tp1_r || 0}× SL distance.
                </p>
              </div>
              <div>
                <Label>Default TP2 (R multiple)</Label>
                <input
                  data-testid="tp2-r-input"
                  type="number"
                  step="0.1"
                  min="0"
                  className={inputCls}
                  value={form.tp2_r}
                  onChange={set("tp2_r")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Used only when the trader leaves TP blank — manual values always win.
                </p>
              </div>
              <div>
                <Label>New SL allowance (%)</Label>
                <input
                  data-testid="sl-allowance-input"
                  type="number"
                  step="1"
                  min="0"
                  max="200"
                  className={inputCls}
                  value={form.sl_allowance_pct ?? 25}
                  onChange={set("sl_allowance_pct")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  How far past First SL a trader may move their stop, as % of the entry→First SL
                  distance.
                </p>
              </div>
              <div>
                <Label>Trailing SL distance (%)</Label>
                <input
                  data-testid="trail-pct-input"
                  type="number"
                  step="1"
                  min="1"
                  max="200"
                  className={inputCls}
                  value={form.trail_pct ?? 25}
                  onChange={set("trail_pct")}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Fixed gap kept below the highest price reached once trailing is switched on.
                </p>
              </div>
              <div>
                <Label>Window timezone</Label>
                <input
                  data-testid="window-tz-input"
                  className={inputCls}
                  value={form.window_tz ?? "Asia/Kolkata"}
                  onChange={set("window_tz")}
                  placeholder="Asia/Kolkata"
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Trading window is judged in this zone · daily counters stay IST.
                </p>
              </div>
              <div>
                <Label>External trade detection</Label>
                <select
                  data-testid="external-action-select"
                  className={inputCls}
                  value={form.external_action ?? "alert"}
                  onChange={set("external_action")}
                >
                  <option value="alert">Alert only</option>
                  <option value="close">Auto-close</option>
                </select>
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Applies to trades opened outside Strict RR or on a disallowed instrument.
                </p>
              </div>
              <div className="sm:col-span-2 lg:col-span-3 rounded-sm border border-emerald-900/60 bg-emerald-500/[0.04] p-3">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <Label>Master instrument list · desk-wide</Label>
                  <span className="px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 text-[9px] uppercase font-bold tracking-wide">
                    Level 1 of 2
                  </span>
                </div>
                <p className="text-[11px] text-zinc-400 mb-2 font-mono leading-relaxed"
                   data-testid="master-list-explainer">
                  This is <span className="text-emerald-300">not</span> a default risk rule — it
                  is the master list of everything this desk may trade. Each trader's own list is
                  chosen <span className="text-emerald-300">from</span> this one, so anything
                  unticked here can never be traded by anyone.
                </p>
                <InstrumentChecklist
                  testid="allowed-instruments"
                  catalog={catalog}
                  onRefresh={resyncCatalog}
                  refreshing={resyncing}
                  value={form.allowed_instruments || []}
                  onChange={(v) => setForm((f) => ({ ...f, allowed_instruments: v }))}
                />
                <p className="text-[11px] text-zinc-600 mt-1.5 font-mono">
                  Ticked from the MT5 broker catalog reported by the connected EAs, using the
                  broker's exact symbol names. Nothing selected = every catalog symbol is
                  tradeable. Anything outside the list triggers the external trade rule.
                </p>
              </div>
            </div>

            <button
              data-testid="save-settings-btn"
              type="submit"
              disabled={saving}
              className="w-fit bg-blue-600 hover:bg-blue-500 text-white px-6 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save template"}
            </button>
          </form>
          </CollapsibleCard>
          </div>

          <div className="col-span-12 lg:col-span-4">
          <CollapsibleCard
            title="Invite a trader"
            icon={UserPlus}
            count={invites.length}
            storageKey="admin-invites"
            testid="invite-card"
          >
          <form
            onSubmit={addInvite}
            data-testid="invite-form"
            className="p-5 flex flex-col gap-4"
          >
            <div>
              <Label>Google email</Label>
              <input
                data-testid="invite-email-input"
                className={inputCls}
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                placeholder="trader@gmail.com"
              />
            </div>
            <div>
              <Label>Role</Label>
              <select
                data-testid="invite-role-select"
                className={inputCls}
                value={invite.role}
                onChange={(e) => setInvite({ ...invite, role: e.target.value })}
              >
                <option value="user">Trader</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              data-testid="invite-submit-btn"
              type="submit"
              className="bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-sm text-xs font-semibold uppercase tracking-[0.14em] transition-colors duration-200 active:scale-95"
            >
              Send invite
            </button>
            <div className="border-t border-zinc-800 pt-3">
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-500 mb-2">
                Invited ({invites.length})
              </p>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="invite-list">
                {invites.length === 0 && (
                  <li className="text-xs text-zinc-600">No invites yet.</li>
                )}
                {invites.map((i) => (
                  <li
                    key={i.email}
                    data-testid={`invite-row-${i.email}`}
                    className="flex items-center justify-between gap-2 font-mono text-xs text-zinc-300"
                  >
                    <Masked
                      value={i.email}
                      kind="email"
                      testid={`invite-email-${i.email}`}
                      className="truncate"
                    />
                    <span className="flex items-center gap-2 shrink-0">
                      <span className="text-[10px] uppercase text-zinc-600">
                        {i.accepted_at ? "joined" : "pending"}
                      </span>
                      <button
                        type="button"
                        data-testid={`remove-invite-btn-${i.email}`}
                        onClick={() => removeInvite(i.email)}
                        className="text-zinc-500 hover:text-red-400 transition-colors duration-200"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <p className="text-[11px] text-zinc-600 leading-relaxed flex gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              Only invited Google accounts can sign in. Each trader connects their own MT5
              credentials and VPS from their desk.
            </p>
          </form>
          </CollapsibleCard>
          </div>
        </div>

        <CollapsibleCard
          title="Traders & performance"
          icon={SlidersHorizontal}
          count={traders.length}
          storageKey="admin-traders"
          testid="users-panel"
        >
          {/* mobile: one card per trader, tap to open the numbers and actions */}
          <div className="md:hidden divide-y divide-zinc-800">
            {traders.length === 0 && (
              <p className="px-4 py-10 text-center text-xs text-zinc-600" data-testid="no-users-mobile">
                No traders yet — invite one above.
              </p>
            )}
            {traders.map((u) => (
              <MobileRow
                key={u.user_id}
                testid={`user-card-${u.email}`}
                title={
                  <>
                    <Masked value={u.name} kind="name"
                            testid={`user-card-name-${u.user_id}`}
                            className="text-xs text-zinc-100" />
                    <Masked value={u.email} kind="email"
                            testid={`user-card-email-${u.user_id}`}
                            className="font-mono text-[10px] text-zinc-500" />
                  </>
                }
                badge={
                  <span
                    className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-bold shrink-0 ${
                      u.mt5_connected
                        ? "bg-emerald-500/10 text-emerald-400"
                        : "bg-red-500/10 text-red-400"
                    }`}
                  >
                    {u.mt5_connected ? "live" : "offline"}
                  </span>
                }
                trailing={
                  <span className={`font-mono text-xs font-bold shrink-0 ${pnlTone(u.pnl_today)}`}
                        data-testid={`user-card-pnl-${u.email}`}>
                    {money(u.pnl_today)}
                  </span>
                }
                rows={[
                  ["MT5 login", u.mt5_login || "—"],
                  ["Status", statusText(u)],
                  ["Week", <span className={pnlTone(u.pnl_week)}>{money(u.pnl_week)}</span>],
                  ["Month", <span className={pnlTone(u.pnl_month)}>{money(u.pnl_month)}</span>],
                  ["Trades", u.max_trades_per_day > 0
                    ? `${u.trades_used_today ?? u.trades_today} / ${u.max_trades_per_day}`
                    : u.trades_today],
                  ["Open / max", `${u.open_trades} / ${u.max_open_trades}`],
                  ["Limit left", `$${(u.loss_limit_remaining ?? 0).toFixed(2)}`],
                  ["Rules", u.has_override ? "override" : "template"],
                ]}
                actions={traderActions(u)}
              />
            ))}
          </div>

          <div className="overflow-x-auto hidden md:block">
            <table className="w-full border-collapse min-w-[1100px]">
              <thead className="bg-black/40">
                <tr>
                  {["Trader", "MT5", "P&L Today", "Week", "Month", "Trades", "Open/Max", "Limit Left", "Rules", "Status", "Actions"].map(
                    (h) => (
                      <th key={h} className={th}>
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {traders.length === 0 && (
                  <tr>
                    <td colSpan={11} className="px-4 py-10 text-center text-xs text-zinc-600" data-testid="no-users">
                      No traders yet — invite one above.
                    </td>
                  </tr>
                )}
                {traders.map((u) => (
                  <Fragment key={u.user_id}>
                    <tr
                      data-testid={`user-row-${u.email}`}
                      className="border-b border-zinc-800 hover:bg-zinc-800/40 transition-colors duration-200"
                    >
                      <td className="px-3 py-2.5 text-xs">
                        <Masked
                          value={u.name}
                          kind="name"
                          testid={`user-name-${u.user_id}`}
                          className="text-zinc-100"
                        />
                        <Masked
                          value={u.email}
                          kind="email"
                          testid={`user-email-${u.user_id}`}
                          className="font-mono text-[11px] text-zinc-500"
                        />
                      </td>
                      <td className={td}>
                        <span
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${
                            u.mt5_connected
                              ? "bg-emerald-500/10 text-emerald-400"
                              : "bg-red-500/10 text-red-400"
                          }`}
                        >
                          {u.mt5_connected ? "live" : "offline"}
                        </span>
                        <span className="ml-2 text-[11px] text-zinc-600">
                          {u.mt5_login || "—"}
                        </span>
                      </td>
                      <td
                        className={`${td} ${
                          u.pnl_today > 0 ? "text-emerald-400" : u.pnl_today < 0 ? "text-red-400" : ""
                        }`}
                        data-testid={`pnl-today-${u.email}`}
                      >
                        {money(u.pnl_today)}
                      </td>
                      <td className={`${td} ${u.pnl_week > 0 ? "text-emerald-400" : u.pnl_week < 0 ? "text-red-400" : ""}`}>
                        {money(u.pnl_week)}
                      </td>
                      <td className={`${td} ${u.pnl_month > 0 ? "text-emerald-400" : u.pnl_month < 0 ? "text-red-400" : ""}`}>
                        {money(u.pnl_month)}
                      </td>
                      <td className={td}>
                        {u.max_trades_per_day > 0
                          ? `${u.trades_used_today ?? u.trades_today} / ${u.max_trades_per_day}`
                          : u.trades_today}
                        {u.daily_profit_target > 0 && (
                          <span
                            className={`ml-2 text-[10px] ${
                              u.profit_target_hit ? "text-emerald-400" : "text-zinc-600"
                            }`}
                          >
                            tgt ${u.daily_profit_target}
                          </span>
                        )}
                      </td>
                      <td className={td}>
                        {u.open_trades} / {u.max_open_trades}
                      </td>
                      <td className={td} data-testid={`limit-left-${u.email}`}>
                        ${(u.loss_limit_remaining ?? 0).toFixed(2)}
                        {u.loss_limit_mode === "trailing" && (
                          <span className="ml-1 text-[10px] text-blue-400">↑</span>
                        )}
                      </td>
                      <td className={td}>
                        <span
                          data-testid={`rules-badge-${u.email}`}
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${
                            u.has_override
                              ? "bg-blue-500/10 text-blue-400"
                              : "bg-zinc-800 text-zinc-400"
                          }`}
                        >
                          {u.has_override ? "override" : "template"}
                        </span>
                        <span className="ml-2 text-[11px] text-zinc-600">
                          {u.effective?.risk_method === "fixed"
                            ? `$${u.effective?.risk_fixed_amount}`
                            : `${u.effective?.risk_percent}%`}{" "}
                          / {u.effective?.window_start}-{u.effective?.window_end} / TP1{" "}
                          {u.effective?.tp1_ratio}% /{" "}
                          {u.effective?.loss_limit_mode === "trailing" ? "trailing" : "fixed"} limit
                        </span>
                      </td>
                      <td className={td}>
                        <span
                          className={`px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold ${
                            u.status === "revoked"
                              ? "bg-red-500/10 text-red-400"
                              : u.kill_switch
                              ? "bg-amber-500/10 text-amber-400"
                              : u.day_stopped
                              ? "bg-blue-500/10 text-blue-400"                              : "bg-emerald-500/10 text-emerald-400"
                          }`}
                        >
                          {u.status === "revoked"
                            ? "removed"
                            : u.kill_switch
                            ? "blocked"
                            : u.day_stopped
                            ? u.day_stop_reason === "auto"
                              ? "limit hit"
                              : "day closed"
                            : "active"}
                        </span>
                      </td>
                      <td className={td}>
                        {traderActions(u)}
                      </td>
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="px-4 py-3 text-[11px] text-zinc-600 border-t border-zinc-800">
            Admin emails come from the backend allowlist (ADMIN_EMAILS); everyone else must be
            invited. Each trader runs on their own MT5 account and VPS, with P&L, loss limit and
            trade count tracked independently.
          </p>
        </CollapsibleCard>
      </div>

      <DeleteTraderDialog
        open={Boolean(deleting)}
        trader={deleting}
        busy={purging}
        onConfirm={purge}
        onClose={() => setDeleting(null)}
      />
    </Shell>
  );
}
